import {
  Banknote,
  Cake,
  Gift,
  LineChart,
  MinusCircle,
  PlusCircle,
  Receipt,
  ShoppingBasket,
  TrendingUp,
  Users,
} from 'lucide-react';
import { brl, decimal } from '@/shared/lib/format';
import type { Passbook } from '@/shared/types';
import { KpiCard } from '@/shared/components/kpi-card';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';
import { EmptyState } from '@/shared/components/empty-state';
import { useSignupInterval, type DashboardSummary } from '../api/use-dashboard';
import { DashboardSeriesChart, type ChartPoint } from './dashboard-charts';

interface NativeDashboardPanelProps {
  idPassbook: string;
  passbook: Passbook;
  summary?: DashboardSummary;
  isLoading: boolean;
}

function toChartPoints(series?: { labels: string[]; data: number[] }): ChartPoint[] {
  if (!series) return [];
  return series.labels.map((label, i) => ({ label, value: series.data[i] ?? 0 }));
}

export function NativeDashboardPanel({
  idPassbook,
  passbook,
  summary,
  isLoading,
}: NativeDashboardPanelProps) {
  const isCashback = Boolean(passbook.hasCashback);
  const s = summary;
  const signupSeries = useSignupInterval(idPassbook, 7, true);
  const signupPoints = toChartPoints(signupSeries.data);

  const formatCredited = (value: number) => (isCashback ? brl(value) : decimal(value));
  const formatRedeemed = (value: number) =>
    isCashback && s?.redeemedLabel.toLowerCase().includes('cashback') ? brl(value) : decimal(value);

  const kpis = [
    {
      label: 'Clientes Fidelizados',
      value: decimal(s?.fidelized ?? 0),
      icon: Users,
      hint: 'Total de clientes fidelizados na base.',
    },
    {
      label: 'Compras',
      value: decimal(s?.purchases ?? 0),
      icon: ShoppingBasket,
      hint: 'Total de compras transacionadas no programa.',
    },
    {
      label: 'Receita',
      value: brl(s?.revenue ?? 0),
      icon: Banknote,
      hint: 'Somatória financeira das compras.',
    },
    {
      label: 'Ticket Médio',
      value: brl(s?.avgTicket ?? 0),
      icon: Receipt,
      hint: 'Receita dividida pela quantidade de compras.',
    },
    {
      label: 'Resgates',
      value: decimal(s?.redemptions ?? 0),
      icon: Gift,
      hint: 'Total de resgates realizados.',
    },
    {
      label: s?.creditedLabel ?? 'Creditados',
      value: formatCredited(s?.pointsCredited ?? 0),
      icon: PlusCircle,
      hint: 'Total creditado/enviado no programa.',
    },
    {
      label: s?.redeemedLabel ?? 'Resgatados',
      value: formatRedeemed(s?.pointsRedeemed ?? 0),
      icon: MinusCircle,
      hint: s?.redeemedLabel ?? 'Total resgatado.',
    },
    {
      label: '% de Resgates',
      value: `${decimal(s?.redemptionRate ?? 0, 2)}%`,
      icon: TrendingUp,
      hint: s?.redemptionRateHint ?? 'Percentual de resgate.',
    },
    {
      label: 'Média de Idade',
      value: `${decimal(s?.averageAge ?? 0, 0)} anos`,
      icon: Cake,
      hint: 'Média de idade desde o início do programa.',
    },
  ];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
        {kpis.map((kpi, index) => {
          const isLastOddOnMobile = index === kpis.length - 1 && kpis.length % 2 === 1;
          return (
            <div
              key={kpi.label}
              className={isLastOddOnMobile ? 'col-span-2 h-full md:col-span-1' : 'h-full'}
            >
              <KpiCard {...kpi} loading={isLoading} index={index} />
            </div>
          );
        })}
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Novos cadastros — últimos 7 dias</CardTitle>
          </CardHeader>
          <CardContent>
            <DashboardSeriesChart
              data={signupPoints}
              loading={signupSeries.isLoading}
              valueFormatter={(v) => decimal(v)}
            />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Cartão digital</CardTitle>
          </CardHeader>
          <CardContent>
            {passbook.shortUrl ? (
              <div className="flex flex-col items-center gap-3">
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=160x160&margin=8&data=${encodeURIComponent(passbook.shortUrl)}`}
                  alt="QR Code do cartão"
                  width={160}
                  height={160}
                  className="rounded-xl bg-white p-2"
                />
                <a
                  href={passbook.shortUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="truncate text-sm text-primary hover:underline"
                >
                  {passbook.shortUrl}
                </a>
              </div>
            ) : (
              <EmptyState
                icon={LineChart}
                title="Sem link de cartão"
                description="Configure o link curto da loja para gerar o QR Code."
                className="py-10"
              />
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
